import { useEffect, useRef, useState } from 'react';
import './TurnstileWidget.css';

/**
 * Cloudflare Turnstile (modo managed, render explícito). A sitekey vem do
 * backend (GET /api/auth/providers → turnstileSiteKey); sem ela o widget
 * não aparece e os formulários seguem como antes.
 */
type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global { interface Window { turnstile?: TurnstileApi } }

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile indisponível')));
    s.onerror = () => { loading = null; reject(new Error('Não foi possível carregar a verificação')); };
    document.head.appendChild(s);
  });
  return loading;
}

let siteKeyPromise: Promise<string | null> | null = null;

/** Sitekey do Turnstile (null = desligado no servidor) */
export function useTurnstileSiteKey() {
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    siteKeyPromise ??= fetch('/api/auth/providers')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d?.turnstileSiteKey as string) || null)
      .catch(() => null);
    let alive = true;
    siteKeyPromise.then((k) => { if (alive) setSiteKey(k); });
    return () => { alive = false; };
  }, []);
  return siteKey;
}

/**
 * Widget do Turnstile. `onToken(null)` quando expira ou dá erro (o widget se
 * reinicia sozinho); `resetKey` muda → novo desafio (ex.: depois de uma
 * tentativa recusada, porque o token é de uso único).
 */
export function TurnstileWidget({ siteKey, action, onToken, resetKey = 0 }: {
  siteKey: string;
  action: 'login' | 'signup' | 'forgot' | 'invite';
  onToken: (token: string | null) => void;
  resetKey?: number;
}) {
  const box = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const cb = useRef(onToken);
  cb.current = onToken;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const dark = document.documentElement.dataset.theme === 'dark' || document.documentElement.classList.contains('dark');
    loadTurnstile().then((t) => {
      if (!alive || !box.current) return;
      widgetId.current = t.render(box.current, {
        sitekey: siteKey,
        action,
        language: 'pt-br',
        theme: dark ? 'dark' : 'light',
        appearance: 'interaction-only',
        callback: (token: string) => cb.current(token),
        'expired-callback': () => { cb.current(null); if (widgetId.current) t.reset(widgetId.current); },
        'error-callback': () => { cb.current(null); return false; },
        'timeout-callback': () => { cb.current(null); if (widgetId.current) t.reset(widgetId.current); },
      });
    }).catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey, action]);

  useEffect(() => {
    if (resetKey && widgetId.current && window.turnstile) { cb.current(null); window.turnstile.reset(widgetId.current); }
  }, [resetKey]);

  return (
    <div className="turnstile-box" aria-live="polite">
      <div ref={box} />
      {failed && <p className="turnstile-box__error" role="alert">Não foi possível carregar a verificação de segurança. Recarregue a página.</p>}
    </div>
  );
}
